import { useState } from 'react'
import type { FC } from 'react'
import { Box, Link, Paper, Skeleton, Typography } from '@mui/material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined'
import type { BffCrmPersonBenefitDocumentsRetrieveResponse } from '@fireflyhealth/api'

import { EmptyState } from '~/shared/ui'
import type { PatientUser } from '~/shared/types'
import { useGetPersonBenefitDocuments } from './PersonBenefitDocumentsService'
import { PlanYearPicker } from '~/entities/health-plan'

// Server-owned shape — reuse the generated response type rather than re-declaring it.
type BenefitDocument = BffCrmPersonBenefitDocumentsRetrieveResponse['documents'][number]

interface BenefitDocumentsProps {
  patient: PatientUser
}

/** The document glyph tile that opens each row, in the info tone. */
const DocumentIconTile: FC = () => (
  <Box
    aria-hidden
    sx={theme => ({
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
      width: 40,
      height: 40,
      borderRadius: '8px',
      bgcolor: theme.palette.tones.info.bg,
      color: theme.palette.tones.info.fg,
      '& svg': { fontSize: '2rem' },
    })}
  >
    <InsertDriveFileOutlinedIcon />
  </Box>
)

/**
 * One plan document. The name opens `link` in a new tab — the openable target is resolved
 * server-side (external `url`, else the stored file, else blank), so this row never re-derives which
 * of the two fields to open. A document the server could not resolve a target for comes back with a
 * blank `link` and renders as plain text rather than a dead anchor.
 */
const DocumentRow: FC<{ document: BenefitDocument; isFirst: boolean }> = ({
  document,
  isFirst,
}) => {
  const { name, description, link } = document

  return (
    <Box
      data-testid={`benefit-document-${name}`}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        px: 2.5,
        py: 2,
        ...(isFirst ? {} : { borderTop: '1px solid', borderColor: 'divider' }),
      }}
    >
      <DocumentIconTile />

      <Box sx={{ minWidth: 0, flexGrow: 1 }}>
        {link ? (
          <Link
            href={link}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`benefit-document-link-${name}`}
            sx={{ fontSize: '1.5rem', fontWeight: 600, textDecoration: 'none' }}
          >
            {name}
          </Link>
        ) : (
          <Typography sx={{ fontSize: '1.5rem', fontWeight: 600, color: 'text.primary' }}>
            {name}
          </Typography>
        )}

        {description && (
          <Typography variant="body2" sx={{ color: 'text.secondary', display: 'block', mt: 0.25 }}>
            {description}
          </Typography>
        )}
      </Box>
    </Box>
  )
}

/** Placeholder rows, sized like the real ones so the list doesn't shift when they're replaced. */
const DocumentRowsSkeleton: FC = () => (
  <Paper
    variant="outlined"
    data-testid="benefit-documents-loading"
    sx={{ borderRadius: '12px', overflow: 'hidden' }}
  >
    {Array.from({ length: 4 }).map((_, index) => (
      <Box
        key={index}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          px: 2.5,
          py: 2,
          ...(index === 0 ? {} : { borderTop: '1px solid', borderColor: 'divider' }),
        }}
      >
        <Skeleton variant="rounded" width={40} height={40} />
        <Box sx={{ flexGrow: 1 }}>
          <Skeleton variant="text" width="40%" />
          <Skeleton variant="text" width="65%" />
        </Box>
      </Box>
    ))}
  </Paper>
)

const BenefitDocumentsTab: FC<{ personId: number | undefined }> = ({ personId }) => {
  // Unset until staff pick a year: the response names the one it answered from.
  const [pickedConfigurationId, setPickedConfigurationId] = useState<number>()

  const {
    data,
    isPending: isLoading,
    isPlaceholderData,
    error,
  } = useGetPersonBenefitDocuments({
    personId,
    policyConfigurationId: pickedConfigurationId,
  })

  const configurations = data?.policy_configurations ?? []
  const documents = data?.documents ?? []

  const renderDocuments = () => {
    if (isPlaceholderData) return <DocumentRowsSkeleton />
    if (documents.length === 0) {
      return (
        <EmptyState
          icon={<InfoOutlinedIcon />}
          title="No benefit documents"
          caption="This plan year has no documents shared with this member."
        />
      )
    }
    return (
      <Paper variant="outlined" sx={{ borderRadius: '12px', overflow: 'hidden' }}>
        {documents.map((document, index) => (
          // The payload carries no id and document names are not guaranteed unique, so the
          // server-fixed position in the list is the only stable key available.
          <DocumentRow
            key={`${document.name}-${index}`}
            document={document}
            isFirst={index === 0}
          />
        ))}
      </Paper>
    )
  }

  if (isLoading) {
    return (
      <Box sx={{ p: 3 }}>
        <DocumentRowsSkeleton />
      </Box>
    )
  }

  if (error) {
    return (
      <EmptyState
        icon={<InfoOutlinedIcon />}
        title="Couldn't load benefit documents"
        caption="Something went wrong fetching this member's plan documents. Please try again."
      />
    )
  }

  if (configurations.length === 0) {
    return (
      <EmptyState
        icon={<InfoOutlinedIcon />}
        title="No benefit documents"
        caption="This member has no plan documents to display, either because no plan configuration was found or none of their documents are shared with members."
      />
    )
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 2, mb: 2 }}>
        <PlanYearPicker
          planYears={configurations}
          selectedPlanYearId={
            pickedConfigurationId ?? data?.selected_policy_configuration_id ?? undefined
          }
          onChange={setPickedConfigurationId}
          idPrefix="benefit-documents"
        />

        {documents.length > 0 && !isPlaceholderData && (
          <Typography
            variant="body2"
            data-testid="benefit-documents-count"
            sx={{ color: 'text.secondary', ml: 'auto', pb: 1 }}
          >
            <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>
              {documents.length}
            </Box>{' '}
            {documents.length === 1 ? 'document' : 'documents'}
          </Typography>
        )}
      </Box>

      {renderDocuments()}
    </Box>
  )
}

// Keyed remount: the route is keyed by path, so a kept response would show the member just left.
const BenefitDocuments: FC<BenefitDocumentsProps> = ({ patient }) => (
  <BenefitDocumentsTab key={patient.person?.id} personId={patient.person?.id} />
)

export default BenefitDocuments
